<template>
    <div class="ds-month-shifts-panel">
        <div class="ds-month-shifts-panel-body">
            <h2 class="ds-month-shifts-panel-title">Скопировать график на месяц</h2>
            <div class="flex flex-col gap-1.5">
                <span class="ds-glass-label">Из {{ sourceMonthLabel }} в</span>
                <div class="flex items-center gap-1.5 relative">
                    <button type="button" class="ds-month-shifts-panel-arrow" aria-label="Предыдущий месяц" @click="prevMonth">
                        <icon width="9" height="13"><IconChevronLeft /></icon>
                    </button>
                    <button type="button" class="ds-month-shifts-panel-month first-letter:uppercase" aria-haspopup="dialog" :aria-expanded="isMonthPickerOpen" @click="isMonthPickerOpen = true">
                        {{ selectedMonthLabel }}
                    </button>
                    <button type="button" class="ds-month-shifts-panel-arrow" aria-label="Следующий месяц" @click="nextMonth">
                        <icon width="9" height="13"><IconChevronRight /></icon>
                    </button>
                    <div v-if="isMonthPickerOpen" class="fixed inset-0 z-40" @click="isMonthPickerOpen = false" />
                    <!-- RD-160: общий month-режим AppCalendar в стеклянном поповере вместо отдельного белого. -->
                    <div v-if="isMonthPickerOpen" class="ds-calendar-popover">
                        <AppCalendar
                            :model-value="newDate"
                            precision="month"
                            @update:model-value="val => { newDate = val as Dayjs; isMonthPickerOpen = false }"
                        />
                    </div>
                </div>
            </div>
            <ScheduleSelect :schedules :selected-count="schedulesCopyList.length" class="ds-month-shifts-panel-list" @selected="onSelect" />
        </div>
        <div class="ds-month-shifts-panel-footer">
            <span class="ds-month-shifts-panel-counter">{{ selectedEmployeesLabel }}</span>
            <button type="button" class="ds-glass-btn ds-glass-btn-secondary" @click="emit('cancel')">Отмена</button>
            <button type="button" class="ds-glass-btn ds-glass-btn-primary" :disabled="isDisabled || isCopyLoading" @click="copy">Скопировать</button>
        </div>
    </div>

    <ShiftCopyModal
        v-if="copyProcess.isModalOpen.value"
        :mode="copyProcess.modalMode.value"
        :errors="copyProcess.modalErrors.value"
        :preview="copyProcess.preview.value"
        :is-loading="copyProcess.isLoading.value"
        @close="copyProcess.close"
        @confirm="copyProcess.confirm"
    />
</template>

<script setup lang="ts">
import dayjs, { type Dayjs } from 'dayjs'
import { ref, computed } from 'vue'
import type { Schedule } from '@/types'
import AppCalendar from '@/ui/AppCalendar.vue'
import IconChevronLeft from '@/ui/icons/IconChevronLeft.vue'
import IconChevronRight from '@/ui/icons/IconChevronRight.vue'
import ScheduleSelect from './ScheduleSelect.vue'
import { useToast } from 'vue-toast-notification'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useAuthStore } from '@/stores/authStore'
import ShiftCopyModal from './ShiftCopyModal.vue'
import { useShiftCopy } from './useShiftCopy'
import { ownRowShifts } from '@/utils/otherDepartmentShifts'

const scheduleStore = useScheduleStore()
const authStore = useAuthStore()
const copyProcess = useShiftCopy()
const emit = defineEmits<{
    copied: []
    cancel: []
}>()

const toast = useToast({ position: 'bottom-right' })
const newDate = ref<Dayjs | undefined>()
const isMonthPickerOpen = ref(false)

const selectedMonthLabel = computed(() => newDate.value ? newDate.value.format('MMMM YYYY') : 'Выберите месяц')
// Родительный падеж месяца даёт формат с днём (locale ru), день затем отбрасывается: «Из октября 2026 в».
const sourceMonthLabel = computed(() => dayjs(scheduleStore.currentScheduleDate).format('D MMMM YYYY').replace(/^\d+\s/, ''))

function prevMonth() {
    newDate.value = (newDate.value ?? dayjs()).subtract(1, 'month').startOf('month')
}
function nextMonth() {
    newDate.value = (newDate.value ?? dayjs()).add(1, 'month').startOf('month')
}
const schedules = computed(() => {
    const departmentId = scheduleStore.currentDepartment?.id
    return scheduleStore.schedules.filter(item => (
        item.department.id === departmentId
        && !item.is_vacancy
        && !item.is_outsource
        && item.user.employments.some(employment => employment.id === item.id && employment.department === departmentId)
        && ownRowShifts(item, departmentId).length > 0
    ))
})
type CopySchedule = Pick<Schedule, 'id'>
const schedulesCopyList = ref<CopySchedule[]>(schedules.value)

const isDisabled = computed(() => (
    !newDate.value
    || !schedulesCopyList.value.length
    || !authStore.permissions?.can_create_schedule
    || dayjs(scheduleStore.currentScheduleDate).format('MM-YYYY') === dayjs(newDate.value).format('MM-YYYY')
))
const isCopyLoading = computed(() => copyProcess.isLoading.value)

const pluralEmployees = (count: number) => {
    const remainder100 = count % 100
    const remainder10 = count % 10
    if (remainder100 >= 11 && remainder100 <= 14) return 'сотрудников'
    if (remainder10 === 1) return 'сотрудник'
    if (remainder10 >= 2 && remainder10 <= 4) return 'сотрудника'
    return 'сотрудников'
}
const selectedEmployeesLabel = computed(() => {
    const count = schedulesCopyList.value.length
    const word = pluralEmployees(count)
    return `${word === 'сотрудник' ? 'Выбран' : 'Выбрано'} ${count} ${word}`
})


const onSelect = (schedules: CopySchedule[]) => {
    schedulesCopyList.value = schedules
}

const copyDatePairs = (targetMonth: Dayjs) => {
    const sourceMonth = dayjs(scheduleStore.currentScheduleDate).startOf('month')
    const targetStart = targetMonth.startOf('month')
    const delta = sourceMonth.day() - targetStart.day()

    return Array.from({ length: sourceMonth.daysInMonth() }, (_, index) => sourceMonth.add(index, 'day'))
        .map(sourceDate => ({ sourceDate, targetDate: targetStart.date(sourceDate.date() + delta) }))
        .filter(({ targetDate }) => targetDate.isSame(targetStart, 'month'))
}

const copy = async () => {
    if (!newDate.value) return
    if (!authStore.permissions?.can_create_schedule) {
        toast.error('У вас нет прав на данное действие')
        return
    }

    const targetMonth = newDate.value.startOf('month')
    const pairs = copyDatePairs(targetMonth)
    const departmentId = scheduleStore.currentDepartment?.id
    if (!pairs.length || !departmentId) return

    const sourceSchedules = schedulesCopyList.value
        .map(({ id }) => scheduleStore.schedules.find(schedule => schedule.id === id))
        .filter((schedule): schedule is Schedule => Boolean(schedule))
    if (!sourceSchedules.length) {
        toast.error('Нет доступных сотрудников для копирования')
        return
    }

    await copyProcess.start({
        department_id: departmentId,
        employment_ids: sourceSchedules.map(schedule => schedule.id),
        source_dates: pairs.map(({ sourceDate }) => sourceDate.format('YYYY-MM-DD')),
        target_dates: pairs.map(({ targetDate }) => targetDate.format('YYYY-MM-DD')),
    }, () => {
        scheduleStore.setMonthContext(targetMonth)
        emit('copied')
    }, { confirmOnPreview: true })
}
</script>
